// Modules
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getLang } from 'redux-pagan';
import get from 'lodash/get';
// Store
import {
  changeLightboxActiveSlide,
  translateDescription,
  toggleDescriptionTranslation,
  detectDescriptionLanguage,
  resetDescriptionTranslate
} from '../../redux/modules/propertyDetails';
import { toggleFavorite as toggleFavoriteProperty } from '../../redux/modules/favoriteProperties';
import { addPropertyNote as addPropertyNoteItem } from '../../redux/modules/propertyNotes';
// Components
import PropertyDetails from '../../components/PropertyDetailedInfo/PropertyDetails/PropertyDetails';
// Helpers
import checkRtlLanguage from '../../utils/checkRtlLanguage';
import fetchPropertyNoteById from '../../utils/fetchPropertyNoteById';
import { AnalyticsEventService } from '../../services';
import { trackEvent } from '../../utils/analytics/analyticsManager';
import { eventCategory, eventAction, eventLabel } from '../../utils/analytics/analyticsConstants';

const PropertyDetailsContainer = ({
  locale,
  translate,
  isMobile,
  isRtl,
  isNative,
  propertyData,
  enPropertyType,
  isLightboxOpen,
  currentImage,
  preloadData,
  isPreview,
  similarProperties,
  descriptionData,
  isFavorite,
  propertyNote,
  userToken,
  overrideCurrency,
  changeLightboxActiveSlide: changeLightboxSlide,
  translateDescription: translateDesc,
  toggleDescriptionTranslation: toggleDescTranslation,
  detectDescriptionLanguage: detectDescLang,
  resetDescriptionTranslate: resetDescTranslate,
  toggleFavorite,
  addPropertyNote,
  className,
  isModal,
  handlePopupClose,
  handleEdit,
  routeData
}) => {
  const handleToggleFavorite = () => {
    trackEvent(
      eventCategory.favorites,
      isFavorite ? eventAction.remove : eventAction.add,
      isModal ? eventLabel.propertyDetailsModal : eventLabel.propertyDetailsPage
    );
    toggleFavorite({ property: propertyData, token: userToken });
  };

  const handleAddPropertyNote = (note) => {
    trackEvent(eventCategory.notes, eventAction.add, eventLabel.propertyDetailsModal);
    addPropertyNote({
      noteData: { id: get(propertyData, 'id'), note },
      token: userToken
    });
  };

  return (
    <PropertyDetails
      locale={locale}
      translate={translate}
      isMobile={isMobile}
      isRtl={isRtl}
      isNative={isNative}
      propertyData={propertyData}
      enPropertyType={enPropertyType}
      isLightboxOpen={isLightboxOpen}
      currentImage={currentImage}
      preloadData={preloadData}
      isPreview={isPreview}
      similarProperties={similarProperties}
      descriptionData={descriptionData}
      isFavorite={isFavorite}
      propertyNote={propertyNote}
      overrideCurrency={overrideCurrency}
      changeLightboxActiveSlide={changeLightboxSlide}
      translateDescription={translateDesc}
      toggleDescriptionTranslation={toggleDescTranslation}
      detectDescriptionLanguage={detectDescLang}
      resetDescriptionTranslate={resetDescTranslate}
      toggleFavorite={handleToggleFavorite}
      addPropertyNote={handleAddPropertyNote}
      incrementClicks={AnalyticsEventService.createPropertyEvent}
      className={className}
      isModal={isModal}
      handlePopupClose={handlePopupClose}
      handleEdit={handleEdit}
      routeData={routeData}
    />
  );
};

PropertyDetailsContainer.propTypes = {
  className: PropTypes.string,
  isModal: PropTypes.bool,
  handlePopupClose: PropTypes.func,
  handleEdit: PropTypes.func,
  routeData: PropTypes.object,
  locale: PropTypes.string,
  translate: PropTypes.func,
  isMobile: PropTypes.bool,
  isRtl: PropTypes.bool,
  isNative: PropTypes.bool,
  propertyData: PropTypes.object,
  enPropertyType: PropTypes.string,
  isLightboxOpen: PropTypes.bool,
  currentImage: PropTypes.number,
  preloadData: PropTypes.object,
  isPreview: PropTypes.bool,
  similarProperties: PropTypes.array,
  descriptionData: PropTypes.object,
  isFavorite: PropTypes.bool,
  propertyNote: PropTypes.string,
  userToken: PropTypes.string,
  overrideCurrency: PropTypes.string,
  changeLightboxActiveSlide: PropTypes.func,
  translateDescription: PropTypes.func,
  toggleDescriptionTranslation: PropTypes.func,
  detectDescriptionLanguage: PropTypes.func,
  resetDescriptionTranslate: PropTypes.func,
  toggleFavorite: PropTypes.func,
  addPropertyNote: PropTypes.func
};

const mapStateToProps = ({
  lang,
  propertyDetails,
  favoriteProperties,
  propertyNotes,
  native,
  userAuthentication,
  windowDimentions,
  currency
}) => {
  const translate = getLang(lang, 'static');
  const data = propertyDetails.data;
  const translatedPropertyType = (data && data.property_type)
    ? translate('propertyTypes', data.property_type)()
    : '';

  return {
    locale: lang.locale,
    translate,
    isMobile: windowDimentions.isMobile,
    isRtl: checkRtlLanguage(lang.locale),
    isNative: native.isNative,
    propertyData: data ? { ...data, property_type: translatedPropertyType } : null,
    enPropertyType: (data && data.property_type) || '',
    isLightboxOpen: propertyDetails.isLightboxOpen,
    currentImage: propertyDetails.currentImage,
    preloadData: propertyDetails.preloadData,
    isPreview: propertyDetails.isPreview,
    similarProperties: propertyDetails.similarProperties,
    descriptionData: {
      translatedDescriptionRequired: propertyDetails.translatedDescriptionRequired,
      descriptionLoaded: propertyDetails.descriptionLoaded,
      translatedDescriptionText: propertyDetails.translatedDescriptionText,
      showOriginalDescription: propertyDetails.showOriginalDescription,
      descriptionLoading: propertyDetails.descriptionLoading
    },
    isFavorite: (favoriteProperties.favoriteIDs || []).indexOf(data && data.id) !== -1,
    propertyNote: fetchPropertyNoteById(propertyDetails.propertyId, propertyNotes.data),
    userToken: get(userAuthentication, 'data.access_token', null),
    overrideCurrency: currency.currency
  };
};

const mapDispatchToProps = {
  changeLightboxActiveSlide,
  translateDescription,
  toggleDescriptionTranslation,
  detectDescriptionLanguage,
  resetDescriptionTranslate,
  toggleFavorite: toggleFavoriteProperty,
  addPropertyNote: addPropertyNoteItem
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyDetailsContainer);
